/** Code is partially take from old Kinetic code, to support favorite items that are fully reworked in uxs-platform.
 * https://epicor-corp.visualstudio.com/Epicor-Mobile/_git/EXF?path=/projects/kinetic/src/lib/ep-menu-favorites/ep-menu-favorites.service.ts */
import { Injectable, TemplateRef } from '@angular/core';
import {
  EpDialogAction,
  EpDialogService,
  EpLocalStorageService,
  IEpDialogOptions,
  IEpMenuItem
} from '@epicor/ux-platform';
import { AppLocalizationStrings } from '../app.localization.strings';
import { IMenuFavoriteItem, IMenuFavoritesSet } from '../models/menu-favorites.interface';

@Injectable({
  providedIn: 'root'
})
export class MenuFavoriteService {
  strings = AppLocalizationStrings;

  constructor(private epLocStorage: EpLocalStorageService, private epDialog: EpDialogService) {}

  getMenuSet(menuSetId: string): IMenuFavoritesSet {
    return this.epLocStorage.get(this.getStoreKey(menuSetId)) ?? { menuSetId, items: [], lastFolderId: '' };
  }

  removeItems(menuSetId: string, items: IMenuFavoriteItem[]) {
    if (items.length > 0 && menuSetId) {
      const removedItems: IMenuFavoriteItem[] = [];
      const store = this.getOrCreateMenuSet(menuSetId);
      items.forEach(fav => {
        const idx = store.items.findIndex(m => m.id === fav.id);
        if (idx >= 0) {
          //remove item from array
          const remItems = store.items.splice(idx, 1);
          removedItems.push(remItems[0]);
        }
      });
      const storeKey = this.getStoreKey(menuSetId);
      this.epLocStorage.set(storeKey, store);
    }
  }

  updateItems(menuSetId: string, items: IMenuFavoriteItem[], lastFolderId?: string) {
    if (items.length > 0 && menuSetId) {
      const store = this.getOrCreateMenuSet(menuSetId);
      items.forEach(fav => {
        const item = store.items.find(m => m.id === fav.id);
        if (item) {
          if (fav.seq === undefined) {
            fav.seq = this.findNextSeqInParent(store.items, fav.parentId);
          }
          Object.assign(item, fav);
        } else {
          if (fav.seq === undefined) {
            fav.seq = this.findNextSeqInParent(store.items, fav.parentId);
          }
          store.items.push(fav);
        }
      });
      if (lastFolderId) {
        if (store.items.find(m => m.isFolder && m.id === lastFolderId)) {
          store.lastFolderId = lastFolderId;
        }
      }
      const storeKey = this.getStoreKey(menuSetId);
      this.epLocStorage.set(storeKey, store);
    }
  }

  renameFolder(menuSetId: string, id: string, newName: string) {
    const store = this.getOrCreateMenuSet(menuSetId);

    const folder = store.items.find(i => i.id === id && i.isFolder);
    if (folder) {
      folder.caption = newName;
    }
    const storeKey = this.getStoreKey(menuSetId);
    this.epLocStorage.set(storeKey, store);
  }

  removeFolder(menuSetId: string, id: string) {
    const store = this.getOrCreateMenuSet(menuSetId);

    const folder = store.items.find(i => i.id === id && i.isFolder);
    const arr = <IMenuFavoriteItem[]>[];
    if (folder) {
      arr.push(...store.items.filter(i => i.parentId === id));
      arr.push(folder);
      this.removeItems(menuSetId, arr);
    }
  }

  /** Open Edit Dialog for a menu item from Menu Component */
  public editDialogForMenuItem(
    menuSetId: string,
    menuItem: IEpMenuItem,
    path: string,
    data: any,
    template: TemplateRef<any>
  ): Promise<boolean> {
    return this.openFavoriteDialog(true, menuSetId, menuItem, path, undefined, data, template);
  }

  /** Open Edit Dialog for a favorite item from Favorite Component */
  public editDialogForFavoriteItem(
    menuSetId: string,
    favItem: IMenuFavoriteItem,
    data,
    template: TemplateRef<any>
  ): Promise<boolean> {
    return this.openFavoriteDialog(false, menuSetId, undefined, '', favItem, data, template);
  }

  private openFavoriteDialog(
    isMenu: boolean,
    menuSetId: string,
    menuItem: IEpMenuItem,
    path: string,
    favoriteItem?: IMenuFavoriteItem,
    data?: any,
    template?: TemplateRef<any>
  ): Promise<boolean> {
    const mset = this.getOrCreateMenuSet(menuSetId);
    const folderItems = this.getFolders(menuSetId);

    if (!folderItems.length) {
      const defaultFolder = {
        id: this.strings.defaultFavFolder.toUpperCase(),
        caption: this.strings.defaultFavFolder,
        isFolder: true
      };

      folderItems.push(defaultFolder);
      this.updateItems(menuSetId, folderItems, defaultFolder.id);

      mset.lastFolderId = defaultFolder.id;
    }

    const allowRootFolder = false;

    let itemCaption = '';
    let isNewMenuFavItem = false;
    let favItem: IMenuFavoriteItem;
    if (!isMenu) {
      // coming from Favorite Component
      favItem = favoriteItem;
      itemCaption = favItem.caption;
    } else {
      // coming from Menu Component
      favItem = mset.items.find(f => f.menuId === menuItem.id);
      itemCaption = menuItem.caption;
      if (!favItem) {
        favItem = this.menuItemToFavItem(menuItem, path, undefined);
        isNewMenuFavItem = true;
      }
    }

    const searchParentId = favItem ? favItem.parentId : '';
    let favItemParent = mset.items.find(f => f.id === searchParentId);
    let favItemParentId = favItemParent ? favItemParent.id : '';

    const defaultItemCaption = allowRootFolder ? this.strings.rootFolder : this.strings.selectFolder;

    if (!favItemParentId && mset.lastFolderId) {
      const ff = folderItems.find(f => f.id === mset.lastFolderId);
      if (ff) {
        favItemParentId = ff.id;
        favItemParent = ff;
      }
    }

    data.name = itemCaption;
    data.folder = favItemParentId;
    data.folders = folderItems;
    data.defaultItem = { caption: defaultItemCaption, id: '' };

    const dlg = this.epDialog.info(<IEpDialogOptions>{
      title: this.strings.editDialogTitle,
      icon: 'mdi mdi-pencil',
      template: template,
      data: data,
      buttons: [
        { id: 'remove', labelText: this.strings.removeBtn },
        { id: 'save', labelText: this.strings.saveBtn }
      ],
      fnValidateAction: (action: EpDialogAction) => {
        if (action.id === 'remove') {
          if (favItem) {
            this.removeItems(menuSetId, [favItem]);
            if (menuItem) {
              menuItem.favorite = false;
            }
          }
          return true;
        } else if (action.id === 'save') {
          const modifiedItems: IMenuFavoriteItem[] = [];
          let changed = false;
          const assignedFolder = data.isNewFolder ? data.newFolder : data.folder;

          if (menuItem) {
            menuItem.favorite = true;
          }

          if (isNewMenuFavItem) {
            changed = true;
          }

          if (!allowRootFolder && !(assignedFolder || '').trim()) {
            favItem.seq = 1;
            changed = true;
            modifiedItems.push(favItem);
            this.updateItems(menuSetId, modifiedItems);
            return true;
          }

          // Check if label has been changed
          if (favItem.caption !== data.name) {
            favItem.caption = data.name;
            changed = true;
          }

          const fld = folderItems.find(ff => {
            return ff.id === assignedFolder.trim().toUpperCase();
          });
          if (fld && fld.id !== favItem.parentId) {
            // This is an existing folder, just assign it
            favItem.parentId = fld.id;
            favItem.seq = undefined;
            changed = true;
          } else if (!fld) {
            if (data.isNewFolder) {
              // This is trully a new folder
              const id = data.newFolder.trim().toUpperCase();
              const newFolderItem = <IMenuFavoriteItem>{
                id: id,
                parentId: '',
                caption: data.newFolder.trim(),
                isFolder: true
              };
              modifiedItems.push(newFolderItem);
              favItem.parentId = id;
              favItem.seq = 1;
              changed = true;
            } else {
              return false;
            }
          }

          if (changed) {
            modifiedItems.push(favItem);
            this.updateItems(menuSetId, modifiedItems, favItem.parentId);
          }
          return true;
        }
        return false;
      }
    });

    return dlg.promise.then(v => v.action.id === 'save');
  }

  private getFolders(menuSetId: string): IMenuFavoriteItem[] {
    const mset = this.getMenuSet(menuSetId);
    return mset ? mset.items.filter(m => m.isFolder) : [];
  }

  private menuItemToFavItem(menuItem: IEpMenuItem, path: string, seq: number): IMenuFavoriteItem {
    return <IMenuFavoriteItem>{
      id: menuItem.id,
      parentId: '',
      menuId: menuItem.id,
      caption: menuItem.caption,
      path: path,
      seq: seq,
      isFolder: false
    };
  }

  private getStoreKey(menuSetId: string) {
    return 'ep-menu-favorites.' + menuSetId;
  }

  private getOrCreateMenuSet(menuSetId: string): IMenuFavoritesSet {
    let mset = this.epLocStorage.get(this.getStoreKey(menuSetId));
    if (!mset) {
      mset = <IMenuFavoritesSet>{
        menuSetId: menuSetId,
        items: []
      };
    }
    return mset;
  }

  private findNextSeqInParent(items: IMenuFavoriteItem[], parentId: string) {
    const sorted = items
      .filter(m => m.parentId === parentId)
      .sort((a, b) => {
        const seqA: number = a.seq === undefined ? -1000 : a.seq;
        const seqB: number = b.seq === undefined ? -1000 : b.seq;
        if (seqA > seqB) {
          return 1;
        } else if (seqA < seqB) {
          return -1;
        }
        return 0;
      });
    const seq = sorted && sorted.length ? sorted[sorted.length - 1].seq + 1 : 1;
    return seq;
  }
}
